import { AccessibilityQuery } from "../models/Accessibility";
import { AccessibilityUnitLevelQuery } from "../models/AccessibilityLevel";
import { AccessibilityUnitsQuery } from "../models/AccessibilityUnits";
import { Property, PropertyQuery } from "../models/Property";
import { AccessibilityInfoQuery } from "../models/AccessibilityInfo";
import { PropertyAccessibilityImagesQuery } from "../models/PropertyAccessibilityImages";
import { PropertySubCategoryListQuery } from "../models/PropertySubCategoryList"

import { A11yStandardType } from "../models/A11yStandard";
import { baseUrl, BasicQuerySearch, fetchAndParse, generateQueryString, headers, qmsUrl } from "./utils"
import {QueryPropertyFreeTextInfo} from "../models/PropertyFreeTextInfo";
const QUERY_LIMIT = 1999;


export const searchProperties = (query: BasicQuerySearch & {
    building_area?: string;
    a11y_standard?: string;
    related_to?: string;
    fields?: string;
}) => {
    const queryText = generateQueryString({...query, published: true});
    const url = new URL(`/properties/${queryText}`, baseUrl);
    return fetchAndParse<PropertyQuery>(url.toString(), { method: "GET", headers });
}

export const retrieveProperty = (id: string) => {
    const url = new URL(`/properties/${id}/`, baseUrl);
    return fetchAndParse<Property>(url.toString(), { method: "GET", headers })
}

export const retrieveAccessibilityUnits = (propertyId: string) => {
    const url = new URL(`/accessibility-units/?property__id=${propertyId}&limit=${QUERY_LIMIT}&is_active=true`, baseUrl);
    return fetchAndParse<AccessibilityUnitsQuery>(url.toString(), { method: "GET", headers })
}

export const retrieveAccessibilityUnitsByBuildingAreaId = (buildingAreaId: string, fields: string) => {
    const url = new URL(`/accessibility-units/?property__building_area=${buildingAreaId}&limit=${QUERY_LIMIT}&is_active=true&fields=${fields}`, baseUrl);
    return fetchAndParse<AccessibilityUnitsQuery>(url.toString(), { method: "GET", headers })
}

export const retrieveAccessibilityUnitsWithFields = (propertyId: string, fields: string) => {
    const url = new URL(`/accessibility-units/?property__id=${propertyId}&fields=${fields}&limit=${QUERY_LIMIT}&is_active=true`, baseUrl);
    return fetchAndParse<AccessibilityUnitsQuery>(url.toString(), { method: "GET", headers })
}

export const retrieveAccessibilityUnitsByTypeWithFields = (propertyId: string, type: string, fields: string) => {
    const url = new URL(`/accessibility-units/?property__id=${propertyId}&type=${type}&fields=${fields}&limit=${QUERY_LIMIT}&is_active=true`, baseUrl);
    return fetchAndParse<AccessibilityUnitsQuery>(url.toString(), { method: "GET", headers })
}

export const retrieveRelatedAccessibilityUnitsByUnit = (id: string) => {
    const url = new URL(`/accessibility-units/?related_to=${id}&limit=${QUERY_LIMIT}&is_active=true`, baseUrl);
    return fetchAndParse<AccessibilityUnitsQuery>(url.toString(), { method: "GET", headers })
}

export const retrieveAccessibilityByPropertyId = (propertyId: string, fields:string) => {
    const url = new URL(`/accessibilities/?accessibility_unit__property__id=${propertyId}&limit=${QUERY_LIMIT}&fields=${fields}`, baseUrl);
    return fetchAndParse<AccessibilityQuery>(url.toString(), { method: "GET", headers });
}

export const retrieveAccessibilityByUnitId = (unitId: string) => {
    const url = new URL(`/accessibilities/?accessibility_unit__id=${unitId}&limit=${QUERY_LIMIT}`, baseUrl);
    return fetchAndParse<AccessibilityQuery>(url.toString(), { method: "GET", headers });
}

export const retrieveAccessibilityByBuildingArea = (buildingAreaId: string, fields: string) => {
    const url = new URL(`/accessibilities/?accessibility_unit__property__building_area=${buildingAreaId}&limit=${QUERY_LIMIT}&is_absent=false&fields=${fields}`, baseUrl);
    return fetchAndParse<AccessibilityQuery>(url.toString(), { method: "GET", headers });
}

export const retrieveAccessibilityUnitsLevelsByPropertyId = (propertyId: string) => {
    const url = new URL(`/accessibility-unit-levels/?accessibility_unit__property__id=${propertyId}&limit=${QUERY_LIMIT}`, baseUrl);
    return fetchAndParse<AccessibilityUnitLevelQuery>(url.toString(), { method: "GET", headers });
}

export const retrieveAccessibilityUnitsLevelsByPropertyIdAndType = (propertyId: string, type: string) => {
    const url = new URL(`/accessibility-unit-levels/?accessibility_unit__property__id=${propertyId}&accessibility_unit__type=${type}&limit=${QUERY_LIMIT}`, baseUrl);
    return fetchAndParse<AccessibilityUnitLevelQuery>(url.toString(), { method: "GET", headers });
}

export const retrieveAccessibilityUnitsLevelsByUnitId = (unitId: string) => {
    const url = new URL(`/accessibility-unit-levels/?accessibility_unit=${unitId}&limit=${QUERY_LIMIT}`, baseUrl);
    return fetchAndParse<AccessibilityUnitLevelQuery>(url.toString(), { method: "GET", headers });
}

export const retrieveQuestionnaireStandards = (key: string) => {
    const url = new URL(`/questionnaires-a11y-standards/?questionnaire__key=${key}&limit=1`, qmsUrl);
    return fetchAndParse<A11yStandardType[]>(url.toString(), { method: "GET" });
}

export const retrievePropertyAccessibilityInfoByPropertyId = (id: string) => {
    const url = new URL(`/property-accessibility-infos/?property__id=${id}&is_displayed=true`, baseUrl);
    return fetchAndParse<AccessibilityInfoQuery>(url.toString(), { method: "GET", headers });
}

export const retrievePropertyFreeTextInfo = (id: string) => {
    const url = new URL(`/property_free_text_infos/?property=${id}&is_displayed=true`, baseUrl);
    return fetchAndParse<QueryPropertyFreeTextInfo>(url.toString(), { method: "GET", headers });
}

export const listAccessibilityImagesByPropertyId = (id: string) => {
    const url = new URL(`/accessibility-images/?accessibility_unit__property__id=${id}&is_display_in_output_service=true&limit=${QUERY_LIMIT}&accessibility_unit__is_active=true`, baseUrl);
    return fetchAndParse<PropertyAccessibilityImagesQuery>(url.toString(), { method: "GET", headers });
}

export const retrievePropertiesByRelatedId = (relatedId: string) => {
    // we are limiting it to 1 since we only need to know how many are they ( from the countObject )
    const url = new URL(`/properties/?related_to=${relatedId}&limit=1`, baseUrl);
    return fetchAndParse<PropertyQuery>(url.toString(), { method: "GET", headers });
}

export const retrieveBuildingAreaAccessibilityInfos = (buildingAreaId: string) => {
    const url = new URL(`/building-area-accessibility-infos/?building_area=${buildingAreaId}&limit=${QUERY_LIMIT}`, baseUrl);
    return fetchAndParse<AccessibilityInfoQuery>(url.toString(), { method: "GET", headers });
}

export const retrievePropertyAccessibilityByType = (propertyId: string, accessibilityType: string, filter? : string) => {
    const url = new URL(`/accessibility-unit-levels/?accessibility_unit__type=${accessibilityType}&accessibility_unit__property__id=${propertyId}&fields=${filter}`, baseUrl);
    return fetchAndParse<AccessibilityUnitLevelQuery>(url.toString(), { method: "GET", headers })
}

export const retrievePropertySubCategoryByKey = (key: string,) => {
    const url = new URL(`/property-sub-categories/?key=${key}&limit=1`, baseUrl);
    return fetchAndParse<PropertySubCategoryListQuery>(url.toString(), { method: "GET", headers })
}
